import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Box,
  Button,
  Chip,
  OutlinedInput,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import * as React from "react";
import { debounce } from "throttle-debounce";
import { v4 as uuidv4 } from "uuid";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { StringMultiByEpisodeMetadataInfos } from "../../../Core/Api/Types/string-multi-by-episode-metadata-infos";
import { Distinct, UserHasAcl } from "../../../Utils/helpers";
import { DataGrid } from "../../Table/styled";
import { MetadataInputProps } from "../metadata-input-index";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { useTranslation } from "react-i18next";
import DataTable from "../../Table/table";

interface RowData extends StringMultiByEpisodeMetadataInfos {
  id: string;
}

const MetadataInputStringMultiByEp = React.forwardRef<any, MetadataInputProps>((props, ref) => {
  const { t } = useTranslation();
  const { onChange: _onChange, data, value, episodesTotalNb } = props;

  const userCanAddValue = React.useMemo(
    () =>
      (data.allowPopulate as Boolean) &&
      (UserHasAcl(AclTypes.aclMetadataFieldPopulate) || UserHasAcl(AclTypes.aclSuperUser)),
    []
  );

  const metaValue: StringMultiByEpisodeMetadataInfos[] = React.useMemo(() => {
    return value?.value || [];
  }, [value?.value]);

  const rowsData: RowData[] = React.useMemo(() => {
    return metaValue.map((x) => ({ ...x, id: uuidv4() }));
  }, [metaValue]);

  const availableValues = React.useMemo(() => {
    return (
      data.values
        ?.flatMap((x) => x.valueObject!)
        .flatMap((x) => x.value)
        .flatMap((x) => x.value)
        .filter(Distinct)
        .sort((a, b) => a.localeCompare(b)) || []
    );
  }, [data.values]);

  const handleAddNewValue = React.useCallback(() => {
    const newValue: StringMultiByEpisodeMetadataInfos = {
      episodeNb: Math.max(...metaValue.map((x) => x.episodeNb), 0) + 1,
      value: [],
    };
    _onChange([...metaValue, newValue], data.id!);
  }, [metaValue]);

  const onChange = React.useCallback(
    (rowId: string, episodeNb: number, newValue: string[]) => {
      // Same indexes between rowsData and metaValue
      const indexOfValue = rowsData.findIndex((x) => x.id === rowId);
      const newValueArray = [...metaValue];
      newValueArray[indexOfValue] = {
        episodeNb: episodeNb < 0 ? 0 : episodeNb,
        value: newValue,
      };
      _onChange(newValueArray, data.id!);
    },
    [rowsData, metaValue]
  );

  const handleRemoveValue = React.useCallback(
    (rowId: string) => {
      const indexOfValue = rowsData.findIndex((x) => x.id === rowId);
      const newValueArray = [...metaValue];
      newValueArray.splice(indexOfValue, 1);
      _onChange(newValueArray, data.id!);
    },
    [metaValue]
  );

  const handleCreateAllLines = React.useCallback(() => {
    if (!episodesTotalNb) return;
    const newValue: StringMultiByEpisodeMetadataInfos[] = [];
    for (let i = 0; i < episodesTotalNb; i++) {
      newValue.push({
        episodeNb: i + 1,
        value: [],
      });
    }
    _onChange(newValue, data.id!);
  }, [metaValue, episodesTotalNb]);

  const columns: GridColDef[] = [
    {
      field: "episodeNb",
      headerName: "Ep Nb",
      flex: 0.15,
      renderCell: (params) => (
        <DebouncedNumberInput
          value={params.value}
          onChangeCallBack={(value) => onChange(params?.row?.id, value, params?.row?.value)}
        />
      ),
    },
    {
      field: "value",
      headerName: t("common:value"),
      flex: 1,
      renderCell: (params) => (
        <Autocomplete
          id={data.tagName!}
          // options management
          options={availableValues}
          isOptionEqualToValue={(option, value) => option === value}
          // value management
          value={params.value || []}
          onChange={(e, value) => {
            onChange(params?.row?.id, params?.row?.episodeNb, value as string[]);
          }}
          onInputChange={(e, value) => {
            e.preventDefault();
            e.stopPropagation();
            return value;
          }}
          // render input
          multiple
          freeSolo={userCanAddValue}
          popupIcon={<ExpandMoreRoundedIcon />}
          renderInput={(params) => (
            <Box>
              {userCanAddValue && (
                <Tooltip title={t("tooltip:title.userCanAddValue_pressEnter")}>
                  <QuestionMarkIcon
                    sx={{ zIndex: 700, position: "absolute", right: 4, bottom: 4, opacity: 0.25 }}
                    fontSize="small"
                  />
                </Tooltip>
              )}
              <TextField
                sx={{ mt: "-24px" }}
                {...params}
                placeholder={t("field:placeholder.typeValuesAndPressEnter")}
              />
            </Box>
          )}
          renderTags={(value, getTagProps: AutocompleteRenderGetTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                sx={{ m: 0.5 }}
                color={!availableValues.includes(option) ? "warning" : "default"}
                onDelete={getTagProps({ index }).onDelete}
              />
            ))
          }
          disableClearable
          sx={{ width: "100%", my: 1, position: "relative" }}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      align: "right",
      flex: 0.1,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          sx={{ mx: 0 }}
          color="error"
          icon={<RemoveIcon />}
          label={t("common:removeRow")}
          onClick={() => handleRemoveValue(params?.row?.id)}
        />,
      ],
    },
  ];

  return (
    <>
      <Stack spacing={1}>
        {episodesTotalNb && !rowsData.length && (
          <Button color="primary" variant="outlined" size="small" onClick={handleCreateAllLines}>
            {t("button:createAllLines")}
          </Button>
        )}
        <DataTable
          noControls
          columns={columns}
          rows={rowsData}
          disableRowSelectionOnClick
          getRowHeight={(params) => "auto"}
          initialState={{
            sorting: {
              sortModel: [{ field: "episodeNb", sort: "asc" }],
            },
          }}
        />
        <Button color="primary" variant="outlined" size="small" onClick={handleAddNewValue}>
          {t("button:addNewValue")}
        </Button>
      </Stack>
    </>
  );
});

export default MetadataInputStringMultiByEp;

const DebouncedNumberInput = (props: { value: number; onChangeCallBack: (value: number) => void }) => {
  const { value, onChangeCallBack } = props;

  const [inputValue, setInputValue] = React.useState<number>(value);

  const debouncedOnChange = React.useMemo(
    () => debounce(300, (newValue) => onChangeCallBack(newValue)),
    [onChangeCallBack]
  );
  const handleValueChange = React.useCallback(
    (newValue: number) => {
      setInputValue(newValue);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange]
  );

  return <OutlinedInput type="number" value={inputValue} onChange={(e) => handleValueChange(Number(e.target.value))} />;
};
